import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { from, of, throwError } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import {
  createMaerskBooking,
  createMaerskBookingFailure,
  createMaerskBookingSuccess,
  updateMaerskBooking,
  updateMaerskBookingSuccess,
  updateMaerskBookingFailure,
  cancelMaerskBooking,
  cancelMaerskBookingSuccess,
  cancelMaerskBookingFailure,
} from './maersk-booking.actions'
import { MaerskBookingService } from 'src/services/data/maersk-booking.service'

function extractErrorMessage(error: any): string | undefined {
  const raw = error?.error?.error?.message ?? error?.error?.message ?? error?.message
  if (Array.isArray(raw)) return raw[0]
  if (typeof raw === 'string') return raw
  return undefined
}

@Injectable()
export class MaerskBookingEffects {
  private actions$ = inject(Actions)
  private bookings = inject(MaerskBookingService)
  private toaster = inject(ToasterService)


  createBooking$ = createEffect(() => this.actions$.pipe(
    ofType(createMaerskBooking),
    switchMap(action =>
      from(this.bookings.sendRequest(action.request)).pipe(
        map(response => createMaerskBookingSuccess({ booking: response.data })),
        tap(() => this.toaster.success('Maersk booking request sent successfully')),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to send Maersk booking request', error)
          this.toaster.error('Unable to send Maersk booking request', extractErrorMessage(error))
          return of(createMaerskBookingFailure({ error }))
        })
      )
    )
  ))

  updateBooking$ = createEffect(() => this.actions$.pipe(
    ofType(updateMaerskBooking),
    switchMap(action =>
      from(this.bookings.updateRequest(action.request, action.reference)).pipe(
        map(response =>
          updateMaerskBookingSuccess({
            reference: action.reference,
            message: response?.message,
          }),
        ),
        tap(() => this.toaster.success('Maersk booking update request sent successfully')),
        catchError(error => {
          console.error('Unable to update Maersk booking request', error);
          this.toaster.error('Unable to update Maersk booking', extractErrorMessage(error));
          return of(updateMaerskBookingFailure({ error }));
        })
      )
    )
  ))

  cancelBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelMaerskBooking),
      switchMap(action =>
        from(this.bookings.cancelBooking(action.bookingReference, action.reason, action.additionalComments)).pipe(
          map(response =>
            cancelMaerskBookingSuccess({
              bookingReference: action.bookingReference,
              message: response?.message,
            }),
          ),
          tap(() => this.toaster.success('Maersk booking cancellation request sent successfully')),
          catchError(error => {
            if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error);
            console.error('Unable to cancel Maersk booking', error);
            this.toaster.error('Unable to cancel Maersk booking', extractErrorMessage(error));
            return of(cancelMaerskBookingFailure({ error }));
          })
        )
      )
    )
  );
}
